// /vendor/settings — per-user vendor portal preferences.
//
// V2b ships a single toggle: "Assignment reminders" → key
// vendor_assignment_reminder. The carrier (process-scheduled-tasks
// handleVendorAcceptReminder) gates on this pref at send time.
//
// IMPORTANT — admin-only toggle: the reminder email goes to vendors.email
// (the vendor business's single company address), not a per-user inbox.
// So a tech personally muting the reminder would silence an email going
// to their teammates' shared address — that's not the right semantics.
// Only admins see this toggle; techs see a quiet placeholder. The
// carrier's mute check is scoped to .eq('role','admin') for the same
// reason.
//
// Other vendor-bound emails (the initial assignment + the admin
// escalation fan-out) are WORKFLOW — muting either would break the
// team's response loop. They are not surfaced as toggles.
//
// Same write-path discipline as the V1 portal Settings:
// vendor_users_self_update RLS + vendor_users_self_update_guard trigger
// restricts vendor sessions to notification_prefs + last_login_at only.
// A tech writing role='admin' through this page is rejected at the
// trigger, not silently dropped.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, AlertTriangle, FileCheck2, Loader2, Upload, Clock } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { toast } from 'sonner'
import {
  uploadVendorComplianceDoc, invokeDocumentScan,
  DOC_TYPE_LABEL, deriveDocState,
  type DocType, type ComplianceState,
} from '@/lib/vendorComplianceDocs'

type EventSlug = 'vendor_assignment_reminder'

interface ToggleRow { slug: EventSlug; label: string; description: string }

const TOGGLES: ToggleRow[] = [
  { slug: 'vendor_assignment_reminder', label: 'Assignment reminders', description: 'A reminder email if you haven\'t responded yet to a pending assignment.' },
]

type Prefs = Partial<Record<EventSlug, boolean>>

export default function VendorSettings() {
  const { vendorUser } = useAuth()
  const queryClient = useQueryClient()
  const prefsQueryKey = useMemo(() => ['vendor-self-prefs', vendorUser?.id], [vendorUser?.id])

  const { data: prefs = {} as Prefs, isLoading } = useQuery({
    queryKey: prefsQueryKey,
    enabled: !!vendorUser?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_users')
        .select('notification_prefs')
        .eq('id', vendorUser!.id)
        .maybeSingle()
      if (error) throw error
      return (data?.notification_prefs ?? {}) as Prefs
    },
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ next }: { slug: EventSlug; next: Prefs }) => {
      const { error } = await supabase
        .from('vendor_users')
        .update({ notification_prefs: next })
        .eq('id', vendorUser!.id)
      if (error) throw error
    },
    onMutate: async ({ next }) => {
      await queryClient.cancelQueries({ queryKey: prefsQueryKey })
      const previous = queryClient.getQueryData<Prefs>(prefsQueryKey)
      queryClient.setQueryData<Prefs>(prefsQueryKey, next)
      return { previous }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous !== undefined) queryClient.setQueryData(prefsQueryKey, ctx.previous)
      toast.error(`Couldn't save preference — ${err instanceof Error ? err.message : 'unknown error'}`)
    },
    onSettled: () => { queryClient.invalidateQueries({ queryKey: prefsQueryKey }) },
  })

  const isOn = (slug: EventSlug): boolean => prefs[slug] !== false

  const onToggle = (slug: EventSlug) => {
    const next: Prefs = { ...prefs, [slug]: !isOn(slug) }
    toggleMutation.mutate({ slug, next })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>Settings</h1>
        <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--ink-tertiary)' }}>Manage what you'd like to be emailed about.</p>
      </header>

      <section style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email notifications</h2>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ink-tertiary)' }}>Pick which emails you'd like to receive. Changes save automatically.</p>

        {vendorUser?.role !== 'admin' ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
            No notification settings apply to your account yet.
          </p>
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {TOGGLES.map((t, idx) => {
            const on = isOn(t.slug)
            return (
              <div key={t.slug}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderTop: idx === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <label htmlFor={`pref-${t.slug}`} style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-primary)', cursor: isLoading ? 'default' : 'pointer' }}>
                    {t.label}
                  </label>
                  <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginTop: 2 }}>{t.description}</span>
                </div>
                <button id={`pref-${t.slug}`} type="button" role="switch" aria-checked={on}
                  aria-label={`${t.label} — ${on ? 'on' : 'off'}`}
                  disabled={isLoading || toggleMutation.isPending}
                  onClick={() => onToggle(t.slug)}
                  style={{
                    position: 'relative', flexShrink: 0, width: 40, height: 22, borderRadius: 999, padding: 0,
                    border: `1px solid ${on ? 'var(--accent-border)' : 'var(--border-default)'}`,
                    background: on ? 'var(--accent)' : 'var(--hover-bg)',
                    cursor: isLoading ? 'default' : 'pointer',
                    transition: 'background 120ms ease, border-color 120ms ease',
                  }}>
                  <span aria-hidden="true"
                    style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 16, height: 16, borderRadius: '50%',
                             background: on ? 'var(--accent-ink)' : 'var(--surface)',
                             boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                             transition: 'left 120ms ease, background 120ms ease' }} />
                </button>
              </div>
            )
          })}
        </div>
        )}

        {vendorUser?.role === 'admin' && (
          <p style={{ margin: '18px 0 0', fontSize: 12, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
            New assignments and admin escalations are workflow — those emails can't be turned off here.
          </p>
        )}
      </section>

      {vendorUser?.role === 'admin' && (
        <VendorComplianceSection
          workspaceId={vendorUser.workspace_id}
          vendorId={vendorUser.vendor_id}
          vendorUserId={vendorUser.id}
        />
      )}
    </div>
  )
}

// ─── Compliance documents section (admin-only) ───────────────────────
// One card per required doc type (from vendors.required_docs) PLUS any
// non-required types that already have an uploaded row (so admins can
// see — and replace — work they've already done). Card shows current
// state via deriveDocState; upload button is disabled while a fresh
// row is in pending_review (the operator clicks Re-upload only when
// the existing row is stale).
//
// Upload optimistic flow:
//   1. Storage upload (vendor admin storage policy verifies the path)
//   2. Row INSERT (vendor_admin_insert table policy verifies role +
//      self-attribution + vendor scope)
//   3. fire-and-forget invoke('vendor-document-scan') → the function
//      flips extracted JSON when it completes. Refetch via React Query
//      30s interval reflects the new extracted blob without manual
//      re-render plumbing.
interface VendorDocRow {
  id: string
  doc_type: DocType
  status: 'pending_review' | 'confirmed' | 'rejected'
  effective_date: string | null
  expiration_date: string | null
  created_at: string
  extracted: Record<string, unknown>
}

interface VendorRequirements { w9?: boolean; cgl?: boolean; commercial_auto?: boolean; workers_comp?: boolean }

const STATE_TOKEN: Record<ComplianceState, { label: string; bg: string; border: string; color: string }> = {
  valid:          { label: 'Valid',          bg: 'var(--positive-dim)', border: 'var(--positive-border)', color: 'var(--positive)' },
  expiring_soon:  { label: 'Expiring soon',  bg: 'var(--warning-dim)',  border: 'var(--warning-border)',  color: 'var(--warning)' },
  expired:        { label: 'Expired',        bg: 'var(--danger-dim)',   border: 'var(--danger-border)',   color: 'var(--danger)' },
  pending_review: { label: 'Awaiting review',bg: 'var(--accent-dim)',   border: 'var(--accent-border)',   color: 'var(--accent-text)' },
  rejected:       { label: 'Re-upload requested', bg: 'var(--danger-dim)', border: 'var(--danger-border)', color: 'var(--danger)' },
  missing:        { label: 'Not uploaded',   bg: 'var(--hover-bg)',     border: 'var(--border-default)',  color: 'var(--ink-tertiary)' },
}

function VendorComplianceSection({ workspaceId, vendorId, vendorUserId }: {
  workspaceId: string; vendorId: string; vendorUserId: string
}) {
  const queryClient = useQueryClient()

  const { data: vendor } = useQuery({
    queryKey: ['vendor-self-required-docs', vendorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendors').select('required_docs')
        .eq('id', vendorId).maybeSingle()
      if (error) throw error
      return data as { required_docs: VendorRequirements } | null
    },
  })

  const { data: docs = [] } = useQuery({
    queryKey: ['vendor-self-compliance', vendorId],
    refetchInterval: 30_000, // background poll catches scan completion
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_documents')
        .select('id, doc_type, status, effective_date, expiration_date, created_at, extracted')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as VendorDocRow[]
    },
  })

  // Latest row per doc_type (the list is created_at DESC, so the first
  // entry per type is the latest).
  const latestByType = useMemo(() => {
    const m: Partial<Record<DocType, VendorDocRow>> = {}
    for (const d of docs) if (!m[d.doc_type]) m[d.doc_type] = d
    return m
  }, [docs])

  // Doc types to display: required ones + any non-required ones with
  // an existing row.
  const requirements = vendor?.required_docs ?? {}
  const ALL_TYPES: DocType[] = ['w9', 'cgl', 'commercial_auto', 'workers_comp']
  const visibleTypes = ALL_TYPES.filter(t => requirements[t] || latestByType[t])

  return (
    <section style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 20 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <ShieldCheck style={{ width: 13, height: 13 }} />
        Compliance documents
      </h2>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ink-tertiary)' }}>
        Upload your W-9 and insurance certificates. Staff reviews and confirms what's extracted.
      </p>

      {visibleTypes.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
          Your service provider hasn't marked any documents as required.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visibleTypes.map(t => (
            <ComplianceCard
              key={t}
              docType={t}
              isRequired={!!requirements[t]}
              row={latestByType[t] ?? null}
              workspaceId={workspaceId}
              vendorId={vendorId}
              vendorUserId={vendorUserId}
              onChange={() => queryClient.invalidateQueries({ queryKey: ['vendor-self-compliance', vendorId] })}
            />
          ))}
        </div>
      )}

      <p style={{ margin: '14px 0 0', fontSize: 11, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
        Documents are stored privately. The system uses AI to read fields and your service provider confirms them.
      </p>
    </section>
  )
}

function ComplianceCard({ docType, isRequired, row, workspaceId, vendorId, vendorUserId, onChange }: {
  docType: DocType; isRequired: boolean; row: VendorDocRow | null;
  workspaceId: string; vendorId: string; vendorUserId: string; onChange: () => void
}) {
  const [busy, setBusy] = useState(false)
  const state = deriveDocState({
    hasRow: !!row,
    status: row?.status ?? null,
    expirationDate: row?.expiration_date ?? null,
    isRequired,
  })
  const cfg = STATE_TOKEN[state]

  const onPick = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setBusy(true)
    const { data, error } = await uploadVendorComplianceDoc({
      workspaceId, vendorId, vendorUserId, docType, file: files[0],
    })
    if (error || !data) {
      setBusy(false)
      toast.error(error?.message ?? 'Upload failed')
      return
    }
    toast.success('Uploaded · scanning…')
    onChange()
    // fire-and-forget scan; UI refreshes via the 30s poll
    invokeDocumentScan(data.id).then(({ error: scanErr }) => {
      if (scanErr) {
        // The function degrades gracefully — even on Anthropic failure,
        // the row is left in pending_review for manual entry. Surface
        // the invocation failure as a warning only.
        console.warn('document scan invoke failed', scanErr)
      }
      onChange()
    })
    setBusy(false)
  }

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{DOC_TYPE_LABEL[docType]}</p>
          {!isRequired && (
            <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--ink-tertiary)' }}>Optional</p>
          )}
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color, borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
          {state === 'pending_review' && <Clock style={{ width: 11, height: 11 }} />}
          {(state === 'expired' || state === 'rejected') && <AlertTriangle style={{ width: 11, height: 11 }} />}
          {state === 'valid' && <FileCheck2 style={{ width: 11, height: 11 }} />}
          {cfg.label}
        </span>
      </div>
      {row?.expiration_date && state !== 'missing' && (
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)' }}>Expires {row.expiration_date}</p>
      )}
      <label htmlFor={`upload-${docType}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '7px 12px', background: 'var(--hover-bg)', border: '1px dashed var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Upload style={{ width: 12, height: 12 }} />}
        {busy ? 'Uploading…' : (row ? 'Upload new version' : 'Upload')}
      </label>
      <input id={`upload-${docType}`} type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
        disabled={busy} onChange={e => onPick(e.target.files)} style={{ display: 'none' }} />
    </div>
  )
}
